/**
 * CryptoHelper class provides encryption/decryption utilities for the client side.
 * It handles encryption of content and management of keys.
 */
export class CryptoHelper {
  // Generates a new key pair for the user
  static async generateKeyPair(): Promise<CryptoKeyPair> {
    return await window.crypto.subtle.generateKey(
      {
        name: "RSA-OAEP",
        modulusLength: 2048,
        publicExponent: new Uint8Array([1, 0, 1]),
        hash: "SHA-256",
      },
      true,
      ["encrypt", "decrypt"]
    );
  }

  // Exports a public key as base64 string
  static async exportPublicKey(key: CryptoKey): Promise<string> {
    const exported = await window.crypto.subtle.exportKey("spki", key);
    return this.arrayBufferToBase64(exported);
  }

  // Exports a private key as base64 string
  static async exportPrivateKey(key: CryptoKey): Promise<string> {
    const exported = await window.crypto.subtle.exportKey("pkcs8", key);
    return this.arrayBufferToBase64(exported);
  }

  // Imports a public key from base64 string
  static async importPublicKey(keyData: string): Promise<CryptoKey> {
    const binaryKey = this.base64ToArrayBuffer(keyData);
    return await window.crypto.subtle.importKey(
      "spki",
      binaryKey,
      {
        name: "RSA-OAEP",
        hash: "SHA-256",
      },
      true,
      ["encrypt"]
    );
  }

  // Imports a private key from base64 string
  static async importPrivateKey(keyData: string): Promise<CryptoKey> {
    const binaryKey = this.base64ToArrayBuffer(keyData);
    return await window.crypto.subtle.importKey(
      "pkcs8",
      binaryKey,
      {
        name: "RSA-OAEP",
        hash: "SHA-256",
      },
      true,
      ["decrypt"]
    );
  }

  // Generates a random content key for symmetric encryption
  static async generateContentKey(): Promise<CryptoKey> {
    return await window.crypto.subtle.generateKey(
      {
        name: "AES-GCM",
        length: 256,
      },
      true,
      ["encrypt", "decrypt"]
    );
  }

  // Exports a content key as base64 string
  static async exportContentKey(key: CryptoKey): Promise<string> {
    const exported = await window.crypto.subtle.exportKey("raw", key);
    return this.arrayBufferToBase64(exported);
  }

  // Imports a content key from base64 string
  static async importContentKey(keyData: string): Promise<CryptoKey> {
    const binaryKey = this.base64ToArrayBuffer(keyData);
    return await window.crypto.subtle.importKey(
      "raw",
      binaryKey,
      {
        name: "AES-GCM",
        length: 256,
      },
      true,
      ["encrypt", "decrypt"]
    );
  }

  // Encrypts a content key with a recipient's public key
  static async encryptContentKey(contentKey: CryptoKey, publicKey: CryptoKey): Promise<string> {
    const exportedContentKey = await window.crypto.subtle.exportKey("raw", contentKey);
    const encryptedKey = await window.crypto.subtle.encrypt(
      {
        name: "RSA-OAEP"
      },
      publicKey,
      exportedContentKey
    );
    return this.arrayBufferToBase64(encryptedKey);
  }

  // Decrypts a content key with the user's private key
  static async decryptContentKey(encryptedKeyBase64: string, privateKey: CryptoKey): Promise<CryptoKey> {
    const encryptedKey = this.base64ToArrayBuffer(encryptedKeyBase64);
    const decryptedKey = await window.crypto.subtle.decrypt(
      {
        name: "RSA-OAEP"
      },
      privateKey,
      encryptedKey
    );
    
    return await window.crypto.subtle.importKey(
      "raw",
      decryptedKey,
      {
        name: "AES-GCM",
        length: 256,
      },
      true,
      ["encrypt", "decrypt"]
    );
  }

  // Encrypts content with a content key
  static async encryptContent(content: string, contentKey: CryptoKey): Promise<string> {
    const encodedContent = new TextEncoder().encode(content);
    const iv = window.crypto.getRandomValues(new Uint8Array(12));
    
    const encryptedContent = await window.crypto.subtle.encrypt(
      {
        name: "AES-GCM",
        iv: iv
      },
      contentKey,
      encodedContent
    );
    
    // Prepend IV to the encrypted content
    const result = new Uint8Array(iv.length + encryptedContent.byteLength);
    result.set(iv);
    result.set(new Uint8Array(encryptedContent), iv.length);
    
    return this.arrayBufferToBase64(result);
  }

  // Decrypts content with a content key
  static async decryptContent(encryptedContentBase64: string, contentKey: CryptoKey): Promise<string> {
    const data = this.base64ToArrayBuffer(encryptedContentBase64);
    const iv = data.slice(0, 12);
    const encryptedContent = data.slice(12);
    
    const decryptedContent = await window.crypto.subtle.decrypt(
      {
        name: "AES-GCM",
        iv: iv
      },
      contentKey,
      encryptedContent
    );
    
    return new TextDecoder().decode(decryptedContent);
  }

  // Derives a key from a password (for master key encryption)
  static async deriveKeyFromPassword(password: string, salt: Uint8Array | ArrayBuffer): Promise<CryptoKey> {
    const encoder = new TextEncoder();
    const passwordKey = await window.crypto.subtle.importKey(
      "raw",
      encoder.encode(password),
      { name: "PBKDF2" },
      false,
      ["deriveBits", "deriveKey"]
    );
    
    // Convert ArrayBuffer to Uint8Array if needed
    const saltArray = salt instanceof ArrayBuffer ? new Uint8Array(salt) : salt;
    
    return await window.crypto.subtle.deriveKey(
      {
        name: "PBKDF2",
        salt: saltArray,
        iterations: 100000,
        hash: "SHA-256"
      },
      passwordKey,
      { name: "AES-GCM", length: 256 },
      true,
      ["encrypt", "decrypt"]
    );
  }

  // Encrypts the master key with a derived key from password
  static async encryptMasterKey(privateKey: CryptoKey, password: string): Promise<string> {
    // Generate a random salt
    const salt = window.crypto.getRandomValues(new Uint8Array(16));
    const derivedKey = await this.deriveKeyFromPassword(password, salt);
    
    // Export the private key
    const exportedPrivateKey = await window.crypto.subtle.exportKey("pkcs8", privateKey);
    
    // Encrypt the private key
    const iv = window.crypto.getRandomValues(new Uint8Array(12));
    const encryptedKey = await window.crypto.subtle.encrypt(
      {
        name: "AES-GCM",
        iv: iv
      },
      derivedKey,
      exportedPrivateKey
    );
    
    // Prepare the result: salt + iv + encryptedKey
    const result = new Uint8Array(salt.length + iv.length + encryptedKey.byteLength);
    result.set(salt);
    result.set(iv, salt.length);
    result.set(new Uint8Array(encryptedKey), salt.length + iv.length);
    
    return this.arrayBufferToBase64(result);
  }

  // Decrypts the master key with a derived key
  static async decryptMasterKey(encryptedMasterKeyBase64: string, derivedKey: CryptoKey): Promise<CryptoKey> {
    try {
      const data = this.base64ToArrayBuffer(encryptedMasterKeyBase64);
      
      // Extract iv and encrypted key (salt not needed for decryption with provided key)
      const iv = new Uint8Array(data.slice(16, 28));
      const encryptedKey = data.slice(28);
      
      // Decrypt the master key using the provided derived key
      const decryptedKey = await window.crypto.subtle.decrypt(
        {
          name: "AES-GCM",
          iv: iv
        },
        derivedKey,
        encryptedKey
      );
      
      // Import the decrypted private key
      return await window.crypto.subtle.importKey(
        "pkcs8",
        decryptedKey,
        {
          name: "RSA-OAEP",
          hash: "SHA-256",
        },
        true,
        ["decrypt"]
      );
    } catch (error) {
      // Safari often throws "The operation failed for an operation-specific reason" 
      // when the passphrase is incorrect or crypto operations fail
      if (error instanceof Error && error.message.includes('operation failed')) {
        throw new Error('Invalid passphrase. Please check your passphrase and try again.');
      }
      throw error;
    }
  }

  // Import a stored passphrase key from base64 string for decryption
  static async importStoredPassphraseKey(passphraseKeyBase64: string): Promise<CryptoKey> {
    return await window.crypto.subtle.importKey(
      "raw",
      this.base64ToArrayBuffer(passphraseKeyBase64),
      { name: "AES-GCM", length: 256 },
      false,
      ["decrypt"]
    );
  }

  // Legacy method: Decrypts the master key with a password (derives key first)
  static async decryptMasterKeyFromPassword(encryptedMasterKeyBase64: string, password: string): Promise<CryptoKey> {
    const data = this.base64ToArrayBuffer(encryptedMasterKeyBase64);
    const salt = new Uint8Array(data.slice(0, 16));
    const derivedKey = await this.deriveKeyFromPassword(password, salt);
    return this.decryptMasterKey(encryptedMasterKeyBase64, derivedKey);
  }

  // Utility: Convert ArrayBuffer to Base64 string
  static arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  // Utility: Convert Base64 string to ArrayBuffer
  static base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }
}